import { Frequency, RRule, rrulestr, Weekday } from 'rrule';
import moment from 'moment';
import { TimeSlot, Event, EventLog } from '../core/_models';
import { capitalize } from '../../../../helpers/funcs';
import { DateTime } from 'luxon';

export const dateStringToDate = function (dt: string) {
  const re = /^(\d{4})(\d{2})(\d{2})(T(\d{2})(\d{2})(\d{2})Z?)?$/;
  const bits = re.exec(dt);

  if (!bits) throw new Error(`Invalid value: ${dt}`);

  return new Date(
    Date.UTC(
      parseInt(bits[1], 10),
      parseInt(bits[2], 10) - 1,
      parseInt(bits[3], 10),
      parseInt(bits[5], 10) || 0,
      parseInt(bits[6], 10) || 0,
      parseInt(bits[7], 10) || 0
    )
  );
};

export const dateToString = function (dt: Date, tz?: string | null) {
  return tz && tz.length > 0 ? dt.toJSON().slice(0, 19) : dt.toJSON();
};

function isNonEmptyArray(variable: any): boolean {
  return Array.isArray(variable) && variable.length > 0;
}

const toRRuleByWeekDay = (w: any) => {
  if (Array.isArray(w)) return new Weekday(w[0], w[1]);
  else return new Weekday(w);
};

export const rruleToHash = (rruleStr?: string) => {
  if (!rruleStr) return undefined;

  const rr = rrulestr(rruleStr);
  const bymonthday = isNonEmptyArray(rr.options.bynmonthday) ? rr.options.bynmonthday : rr.options.bymonthday;
  const byweekday = isNonEmptyArray(rr.options.bynweekday)
    ? rr.options.bynweekday?.map((w: any) => toRRuleByWeekDay(w))
    : rr.options.byweekday?.map((w: any) => toRRuleByWeekDay(w));
  const ret = {
    freq: RRule.FREQUENCIES[rr.options.freq],
    byweekday: byweekday,
    bymonthday: bymonthday,
    bymonth: rr.options.bymonth,
    dtstart: dateToString(rr.options.dtstart, rr.options.tzid),
    tzid: rr.options.tzid,
    until: rr.options.until ? dateToString(rr.options.until, rr.options.tzid) : undefined,
    count: rr.options.count || undefined,
    interval: rr.options.interval,
  };
  return ret;
};

// export const byweekdayToString = (byweekday: string[]) => {
//   const weekdaysMap: { [key: string]: string } = {
//     MO: 'Monday',
//     TU: 'Tuesday',
//     WE: 'Wednesday',
//     TH: 'Thursday',
//     FR: 'Friday',
//     SA: 'Saturday',
//     SU: 'Sunday',
//   };

//   return byweekday.map((d: string) => weekdaysMap[d]).join(', ');
// };

export const byweekdayToString = (byweekday: Weekday[] | undefined) => {
  const weekdaysMap: { [key: number]: string } = {
    0: 'Monday',
    1: 'Tuesday',
    2: 'Wednesday',
    3: 'Thursday',
    4: 'Friday',
    5: 'Saturday',
    6: 'Sunday',
  };

  const dayOfMonethMap: { [key: number]: string } = {
    [-4]: '4th to Last',
    [-3]: '3rd to Last',
    [-2]: '2nd to Last',
    [-1]: 'Last',
    1: '1st',
    2: '2nd',
    3: '3rd',
    4: '4th',
    5: '5th',
  };

  const weekdayToString = (wday: Weekday) => {
    if (wday.n !== undefined) return dayOfMonethMap[wday.n] + ' ' + weekdaysMap[wday.weekday];
    else return weekdaysMap[wday.weekday];
  };

  // return byweekday.map((d: string) => weekdaysMap[d]).join(', ');
  return byweekday?.map((wday) => weekdayToString(wday)).join(', ');
};

export const eventWithTimeToString = (event: Event, time: Date, viewTz?: string) => {
  const ordinalMap = (n: number): string => {
    const suffix = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (suffix[(v - 20) % 10] || suffix[v] || suffix[0]);
  };

  let st = rruleToHash(event.rrule);

  let ret = `${moment(time).format('dddd, MMMM Do YYYY')}\n${moment(time).tz(st?.tzid || moment.tz.guess()).format('LT')} - ${moment(time)
    .add(event.duration, 'minutes')
    .tz(st?.tzid || moment.tz.guess())
    .format('LT (z)')}
  `;

  if (st && st.tzid && st.tzid !== viewTz) {
    ret = `${moment(time).tz(viewTz || moment.tz.guess()).format('dddd, MMMM Do YYYY')}\n${moment(time).tz(viewTz || moment.tz.guess()).format('LT')} - ${moment(
      time
    )
      .add(event.duration, 'minutes')
      .tz(viewTz || moment.tz.guess())
      .format('LT (z)')}
  `;
    ret += `${moment(time).tz(st.tzid).format('LT')} - ${moment(time)
      .add(event.duration, 'minutes')
      .tz(st.tzid)
      .format('LT (z)')}`;
  }

  if (st) {
    if (st.count !== 1) {
      if (st.byweekday) {
        ret += `\n${capitalize(st.freq)} on ${byweekdayToString(st.byweekday)}${
          st.until ? ', until ' + moment(st.until).format('ll') : st.count ? ', ' + st.count + ' times' : ''
        }`;
      } else if (st.bymonthday) {
        ret += `\n${capitalize(st.freq)} on the ${st.bymonthday.map((d) => ordinalMap(d)).join(', ')} of the ${st.freq
          .toLowerCase()
          .replace('ly', '')}${
          st.until ? ', until ' + moment(st.until).format('ll') : st.count ? ', ' + st.count + ' times' : ''
        }`;
      }
      if (st.interval > 1) {
        ret += `\nEvery ${st.interval} ${capitalize(st.freq).replace('ly', '')}${st.interval > 1 ? 's' : ''}`;
      }
    }
  }
  return ret;
};

export const getEventStartDate = (event: Event) => {
  let st = rruleToHash(event.rrule);
  if (st && st.dtstart) return new Date(st.dtstart);
  else return undefined;
};

export const isRecurring = (rruleStr?: string) => {
  let rr = rruleToHash(rruleStr);
  return rr && (rr.count === undefined || rr.count > 1 || (rr.until !== undefined && rr.until.length > 0));
};

export const repeat = <T>(value: T | T[], times: number): (T | T[])[] => {
  /* from rrule.js */
  let i = 0;
  const array: (T | T[])[] = [];

  if (Array.isArray(value)) {
    for (; i < times; i++) array[i] = ([] as T[]).concat(value);
  } else {
    for (; i < times; i++) array[i] = value;
  }
  return array;
};

export const padStart = (/* from rrule.js */ item: string | number, targetLength: number, padString = ' ') => {
  const str = String(item);
  targetLength = targetLength >> 0;
  if (str.length > targetLength) {
    return String(str);
  }

  targetLength = targetLength - str.length;
  if (targetLength > padString.length) {
    padString += repeat(padString, targetLength / padString.length);
  }

  return padString.slice(0, targetLength) + String(str);
};

export const dateToExdate = (date: Date, tz?: string) => {
  if (tz) {
    return moment(date).tz(tz).format('YYYYMMDD[T]HHmmss');
  } else {
    return moment(date).format('YYYYMMDD[T]HHmmss');
  }
};

export const exdateToDates = (exdateStr?: string, tz?: string) => {
  if (!exdateStr || exdateStr.trim().length == 0) return undefined;
  if (exdateStr.includes(',')) return exdateStr.split(',').map((date) => dateToString(dateStringToDate(date), tz));
  else return [dateToString(dateStringToDate(exdateStr), tz)];
};

export const addToExdate = (exdateStr: string, newDate: Date, tz?: string) => {
  if (exdateStr && exdateStr.length > 0) exdateStr = exdateStr && exdateStr.length > 0 ? exdateStr + ',' : ''; // tz + ':';

  exdateStr += dateToExdate(newDate, tz);
  return exdateStr;
};

export const freqToRRuleFreq: Record<string, Frequency> = {
  ['Years']: RRule.YEARLY,
  ['Months']: RRule.MONTHLY,
  ['Weeks']: RRule.WEEKLY,
  ['Days']: RRule.DAILY,
};

export const rRuleFreqToFreq: Record<string, string> = {
  ['YEARLY']: 'Years',
  ['MONTHLY']: 'Months',
  ['WEEKLY']: 'Weeks',
  ['DAILY']: 'Days',
  ['HOURLY']: 'Hours',
  ['MINUTELY']: 'Minutes',
  ['SECONDLY']: 'Seconds',
};

export const weekday = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

export const setRRuleEndData = (rruleStr: string, endDate: Date) => {};

export const combineDateWithTime = (d: Date, t: Date) =>
  new Date(
    Date.UTC(
      d.getFullYear(),
      d.getMonth(),
      d.getDate(),
      t.getHours(),
      t.getMinutes(),
      t.getSeconds(),
      t.getMilliseconds()
    )
  );

export const dateUTC = (d: Date) =>
  new Date(
    Date.UTC(
      d.getFullYear(),
      d.getMonth(),
      d.getDate(),
      d.getHours(),
      d.getMinutes(),
      d.getSeconds(),
      d.getMilliseconds()
    )
  );

export const getDtStartForRrule = (start: Date, tzid: string): Date => {
  const shiftMinutes =
    DateTime.fromJSDate(start).setZone(tzid).offset - DateTime.fromJSDate(start).setZone('local').offset;

  return DateTime.fromJSDate(dateUTC(start)).plus({ minutes: shiftMinutes }).toJSDate();
};

// WST-1075: a month-view (all-day) click carries only a day, and its `date` is that
// day's midnight. Give it the "+" button's default time (next round hour) instead.
export const newSessionStartFromClick = (
  click: { date: Date; dateStr: string; allDay: boolean },
  tzid: string,
  now: Date = new Date()
): Date => {
  if (!click.allDay) {
    const coeff = 1000 * 60 * 30;
    return new Date(Math.floor(click.date.getTime() / coeff) * coeff);
  }
  const { hour } = DateTime.fromJSDate(now).setZone(tzid).plus({ hours: 1 });
  return DateTime.fromISO(click.dateStr.slice(0, 10), { zone: tzid }).set({ hour }).toJSDate();
};

export const formatIsoDateOnly = (date?: Date | null): string | undefined | null => {
  if (date) {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');

    return `${year}-${month}-${day}`;
  }
  return date;
};

// Interface for the result
interface RRuleDayPosition {
  position: number; // e.g., 1 (first), 2 (second), -1 (last)
  rruleDay: number;
  weekday: string; // e.g., "MO", "TU"
  rrule: string;
  text: string;
}

export function getRRuleWeekdayPosition(date: Date): RRuleDayPosition {
  const day = date.getDate(); // Day of the month (1-31)
  const weekday = date.getDay(); // 0 (Sunday) to 6 (Saturday)

  // Map JavaScript weekday (0=Sun, 6=Sat) to rrule weekday (MO, TU, etc.)
  //const weekdayMap: Weekday[] = [Weekday.SU, Weekday.MO, Weekday.TU, Weekday.WE, Weekday.TH, Weekday.FR, Weekday.SA];
  const rruleDay = weekday === 0 ? 6 : weekday - 1;
  const rruleWeekday = new Weekday(rruleDay); //weekdayMap[weekday];

  // Get the first day of the month
  const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  const firstWeekday = firstDayOfMonth.getDay();

  // Calculate the position (1st, 2nd, 3rd, etc.)
  const daysSinceFirst = day - 1; // Days from the 1st to the given date
  const adjustedFirstWeekday = firstWeekday > weekday ? firstWeekday - 7 : firstWeekday;
  const firstOccurrenceDay = 1 + ((weekday - firstWeekday + 7) % 7);
  const position = Math.floor((day - firstOccurrenceDay) / 7) + 1;

  // Get the number of days in the month
  const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const remainingDays = lastDayOfMonth - day;

  const positionText =
    position === 1
      ? '1st'
      : position === 2
      ? '2nd'
      : position === 3
      ? '3rd'
      : position === 4
      ? '4th'
      : position === 5
      ? '5th'
      : position === -1
      ? 'Last'
      : '';

  // Check if it's the last occurrence
  const nextOccurrence = day + 7;
  if (nextOccurrence > lastDayOfMonth) {
    return {
      position: -1,
      weekday: rruleWeekday.toString(),
      rruleDay: rruleDay,
      rrule: new Weekday(rruleDay, -1).toString().replace('+', ''),
      text: 'Last ' + byweekdayToString([rruleWeekday]),
    }; // Last occurrence
  }

  return {
    position,
    weekday: rruleWeekday.toString(),
    rruleDay: rruleDay,
    rrule: new Weekday(rruleDay, position).toString().replace('+', ''),
    text: `${positionText} ${byweekdayToString([rruleWeekday])}`,
  };
}

export const dateInZone = (d: Date, tzid: string) =>
  DateTime.fromObject({
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
    hour: d.getHours(),
    minute: d.getMinutes(),
    second: d.getSeconds(),
    millisecond: d.getMilliseconds(),
  }, { zone: tzid }).toJSDate();

/**
 * WST-723: resolve "include guardians as event attendees by default" given a
 * (provider, providerService) pair. Mirrors the backend resolution at
 * provider/models.py:get_add_guardians_to_events_by_default and
 * scheduling/models.py:_attendees_sync.
 *
 * Precedence (first non-null wins):
 *   1. providerService.parent.add_guardians_to_events_by_default (clinic-level service override)
 *   2. providerService.add_guardians_to_events_by_default (own override)
 *   3. provider.parent_org.add_guardians_to_events_by_default (clinic flag)
 *   4. provider.add_guardians_to_events_by_default (solo provider's own flag)
 *   5. true
 */
export function shouldIncludeGuardiansByDefault(
  providerService?: {
    add_guardians_to_events_by_default?: boolean | null;
    parent?: { add_guardians_to_events_by_default?: boolean | null } | null;
  } | null,
  provider?: {
    add_guardians_to_events_by_default?: boolean;
    parent_org?: { add_guardians_to_events_by_default?: boolean } | null;
  } | null,
): boolean {
  const parentVal = providerService?.parent?.add_guardians_to_events_by_default;
  if (parentVal === true || parentVal === false) return parentVal;
  const ownVal = providerService?.add_guardians_to_events_by_default;
  if (ownVal === true || ownVal === false) return ownVal;
  if (provider?.parent_org && typeof provider.parent_org.add_guardians_to_events_by_default === 'boolean') {
    return provider.parent_org.add_guardians_to_events_by_default;
  }
  if (typeof provider?.add_guardians_to_events_by_default === 'boolean') {
    return provider.add_guardians_to_events_by_default;
  }
  return true;
}

// ── Payment slot (ScheduleViewModal) ──────────────────────────────────

export type PaymentSlotState =
  | { kind: 'subscription' }
  | { kind: 'oneoff'; oneoffUuid: string; editable: boolean }
  | { kind: 'attach'; allowBill: boolean }
  | { kind: 'none' };

/**
 * Decide what the ScheduleViewModal payment slot renders.
 *
 * Order matters:
 * - A subscription always wins — an event can belong to a subscription even
 *   when non-recurring (e.g. a COUNT=1 reschedule child).
 * - A session-level (EventLog) bill is display-only; changing it at the
 *   event level would be wrong.
 * - An event-level bill is editable when payment details are enabled.
 * - Otherwise offer attach buttons; "Attach Bill" only for non-recurring
 *   events, matching the backend guard in scheduling/serializers.py.
 */
export const paymentSlotState = function (
  event: Event,
  eventLog: EventLog | undefined,
  paymentDetailsEnabled: boolean
): PaymentSlotState {
  if (event.subscription) return { kind: 'subscription' };
  if (eventLog?.oneoff?.uuid) return { kind: 'oneoff', oneoffUuid: eventLog.oneoff.uuid, editable: false };
  if (event.oneoff) return { kind: 'oneoff', oneoffUuid: event.oneoff, editable: paymentDetailsEnabled };
  if (!paymentDetailsEnabled) return { kind: 'none' };
  return { kind: 'attach', allowBill: !event.is_recurring };
};

// ── Payment type select (ScheduleEditModal) ───────────────────────────

export type PaymentType = 'plan' | 'bill' | 'no-payment';
export interface PaymentTypeOption {
  value: PaymentType;
  label: string;
}

/**
 * Options for the Payment select. Bill is omitted for recurring events —
 * backend rejects oneoff+infinite rrule (scheduling/serializers.py).
 */
export const paymentTypeOptions = (recurring: boolean): PaymentTypeOption[] =>
  recurring
    ? [
        { value: 'no-payment', label: 'No Payment Selected' },
        { value: 'plan', label: 'Service Plan' },
      ]
    : [
        { value: 'no-payment', label: 'No Payment Selected' },
        { value: 'bill', label: 'Bill' },
        { value: 'plan', label: 'Service Plan' },
      ];

/**
 * Payment-linkage fields the event payload must carry for a payment type.
 * 'no-payment' clears BOTH — fixes stale oneoff/subscription being POSTed.
 */
export const paymentFieldsForType = (
  type: PaymentType,
  prev: { subscription?: string | null; oneoff?: string | null }
): { subscription?: string; oneoff?: string } => {
  switch (type) {
    case 'plan':
      return { subscription: prev.subscription || undefined, oneoff: undefined };
    case 'bill':
      return { subscription: undefined, oneoff: prev.oneoff || undefined };
    case 'no-payment':
      return { subscription: undefined, oneoff: undefined };
  }
};

/** Bill is invalid on recurring events; force it off when recurring flips on. */
export const coercePaymentTypeForRecurring = (type: PaymentType, recurring: boolean): PaymentType =>
  recurring && type === 'bill' ? 'no-payment' : type;
